import { Toggle } from "@base-ui/react/toggle"
import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group"
import classNames from "classnames"
import PropTypes from "prop-types"
import React from "react"

// A segmented control: a short, flat set of mutually exclusive choices, all
// visible at once. Use it where a Select would hide the options behind a click
// and a radio group would spend a whole row on each.
//
// Sizing is in px rather than rem because the app resets the root font size to
// 62.5% — `h-8` is 20px here, not the 32px the design calls for.
const ToggleGroup = ({
  items,
  value,
  onChange,
  ariaLabel,
  invalid = false,
  disabled = false,
}) => (
  <ToggleGroupPrimitive
    aria-label={ariaLabel}
    disabled={disabled}
    value={value ? [value] : []}
    onValueChange={([next]) => {
      // A segmented control always has a selection once one is made. Base UI
      // reports an empty array when the pressed item is pressed again; keep the
      // current value rather than dropping back to nothing.
      if (next === undefined) return

      onChange(next)
    }}
    className={classNames(
      // The border is always present so the invalid state doesn't shift the
      // control by a pixel. red-600 is the same error border FormTextField uses.
      "flex h-[32px] w-full items-center gap-1 rounded-full border p-[2px]",
      "bg-gray-200",
      invalid ? "border-red-600" : "border-transparent"
    )}
    data-design-system="ToggleGroup"
  >
    {items.map((item) => (
      <Toggle
        key={item.value}
        value={item.value}
        className={classNames(
          "h-full flex-1 rounded-full px-3 text-center text-sm transition-colors",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-800",
          item.value === value
            ? "bg-white font-semibold text-gray-900 shadow-sm"
            : "text-gray-600 hover:text-gray-700"
        )}
      >
        {item.label}
      </Toggle>
    ))}
  </ToggleGroupPrimitive>
)

ToggleGroup.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
    })
  ).isRequired,
  // Null renders the group with nothing selected — the correct starting state
  // wherever the choice is deliberately not defaulted.
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  invalid: PropTypes.bool,
  disabled: PropTypes.bool,
}

export default ToggleGroup
